import React from 'react';
import {
  Hand,
  ChevronRight,
  Eye,
  EyeOff,
  User,
  RefreshCw,
  ShieldCheck,
  Users,
  BadgeIndianRupee,
  Package,
  Handshake,
  Clock3,
  Hourglass,
  CalendarDays,
  FileText
} from 'lucide-react';

const avatarColors = ['#10b981', '#f97316', '#8b5cf5', '#3b82f6', '#ec4899'];

const DashboardMobile = ({
  texts,
  loading,
  dashboardMetrics,
  formatCurrency,
  greeting,
  userFirstName,
  healthStatus,
  primaryKpis = [],
  financialCards = [],
  showChipInterest,
  toggleChipInterest,
  collateralDisplayData,
  expiringLoansData,
  overduePaymentsData,
  onQuickValuation,
  onCollateralClick,
  onViewLoans,
  onOpenLoan
}) => {
  const isMetricsLoading = loading && !dashboardMetrics;

  const collateralIcons = {
    blue: Package,
    orange: Handshake,
    purple: Clock3
  };

  return (
    <div className="dash-mobile dash-m-v2">
      <div className="dash-m-hero">
        <h1 className="dash-m-hero-title">
          {greeting || 'Hello'}, {userFirstName || 'User'}! 👋
        </h1>
        <p className="dash-m-hero-sub">
          {texts.heroSubtitle || "Here's what's happening with your business today."}
        </p>
      </div>

      <button type="button" className="dash-m-quick" onClick={onQuickValuation}>
        <span className="dash-m-quick-icon">
          <Hand size={20} />
        </span>
        <span className="dash-m-quick-label">{texts.quickValuation || 'Quick Valuation'}</span>
        <span className="dash-m-chevron">
          <ChevronRight size={16} />
        </span>
      </button>

      <div className="dash-m-chips">
        <div className={`dash-m-chip tone-${healthStatus?.tone || 'good'}`}>
          <ShieldCheck size={16} />
          <div>
            <span>{texts.businessHealth || 'Business Health'}</span>
            <strong>{healthStatus?.label || 'Good'}</strong>
          </div>
        </div>
        <div className="dash-m-chip tone-purple">
          <Users size={16} />
          <div>
            <span>{texts.customers || 'Customers'}</span>
            <strong>{isMetricsLoading ? '…' : (dashboardMetrics?.totalCustomers ?? 0)}</strong>
            <em className="dash-m-chip-sub">
              {isMetricsLoading
                ? '…'
                : `${dashboardMetrics?.activeCustomers ?? 0} ${texts.activeLabel || 'Active'}`}
            </em>
          </div>
        </div>
        <div className="dash-m-chip tone-blue">
          <FileText size={16} />
          <div>
            <span>{texts.totalLoans || 'Total Loans'}</span>
            <strong>{isMetricsLoading ? '…' : (dashboardMetrics?.totalLoans ?? 0)}</strong>
            <em className="dash-m-chip-sub">
              {isMetricsLoading
                ? '…'
                : `${dashboardMetrics?.activeLoans ?? 0} ${texts.activeLabel || 'Active'}`}
            </em>
          </div>
        </div>
        <div className="dash-m-chip tone-green">
          <BadgeIndianRupee size={16} />
          <div>
            <span>{texts.interestEarnedChip || 'Interest (Month)'}</span>
            <strong className="dash-m-chip-money">
              {isMetricsLoading
                ? '…'
                : showChipInterest
                  ? formatCurrency(dashboardMetrics?.totalInterestEarnedThisMonth)
                  : '₹ ****'}
              <button type="button" className="dash-m-eye" onClick={toggleChipInterest}>
                {showChipInterest ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </strong>
          </div>
        </div>
      </div>

      <div className="dash-m-kpi-grid">
        {primaryKpis.map((kpi) => {
          const Icon = kpi.icon;
          return (
            <div key={kpi.key} className={`dash-m-kpi tone-${kpi.tone}`}>
              <div className="dash-m-kpi-top">
                <span>{kpi.label}</span>
                {Icon && <Icon size={16} />}
              </div>
              {isMetricsLoading ? (
                <RefreshCw size={16} className="dash-m-spin" />
              ) : (
                <>
                  <strong>{kpi.count}</strong>
                  <div className="dash-m-kpi-amount">
                    <span>{kpi.showValue ? formatCurrency(kpi.amount) : '₹ ******'}</span>
                    <button type="button" className="dash-m-eye" onClick={kpi.toggle}>
                      {kpi.showValue ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                  {kpi.trend && (
                    <em className={kpi.trendPositive ? 'up' : 'down'}>
                      {kpi.trend} {kpi.trendLabel}
                    </em>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      <div className="dash-m-finance-grid">
        {financialCards.map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.key} className={`dash-m-finance tone-${card.tone}`}>
              <div className="dash-m-finance-top">
                {Icon && <Icon size={16} />}
                <span>{card.label}</span>
              </div>
              {isMetricsLoading ? (
                <RefreshCw size={16} className="dash-m-spin" />
              ) : (
                <>
                  <div className="dash-m-kpi-amount">
                    <strong>{card.showValue ? formatCurrency(card.value) : '₹ ******'}</strong>
                    <button type="button" className="dash-m-eye" onClick={card.toggle}>
                      {card.showValue ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                  {card.trend && (
                    <em className={card.trendPositive ? 'up' : 'down'}>
                      {card.trend} {texts.vsLastMonth || 'vs last month'}
                    </em>
                  )}
                  <small>{card.footer}</small>
                </>
              )}
            </div>
          );
        })}
      </div>

      <div className="dash-m-section">
        <h3 className="dash-m-section-title">{texts.collateralDashboard || 'Collateral Dashboard'}</h3>
        <div className="dash-m-collateral-list">
          {collateralDisplayData.map((item) => {
            const Icon = collateralIcons[item.color] || Package;
            return (
              <button
                type="button"
                key={item.type}
                className={`dash-m-collateral dash-m-collateral-${item.color}`}
                onClick={() => !item.isLoading && onCollateralClick(item.type)}
              >
                <span className="dash-m-collateral-icon">
                  <Icon size={18} />
                </span>
                <span className="dash-m-collateral-text">
                  <span className="dash-m-collateral-label">{item.label}</span>
                  <span className="dash-m-collateral-packets">
                    {item.isLoading ? '...' : item.value}
                  </span>
                </span>
                <span className="dash-m-collateral-amount">
                  {item.isLoading
                    ? '...'
                    : (item.showValue ? formatCurrency(item.loanValue) : '₹ ******')}
                </span>
                <span
                  className="dash-m-eye dash-m-eye-blue"
                  onClick={(e) => {
                    e.stopPropagation();
                    item.toggleVisibility();
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.stopPropagation();
                      item.toggleVisibility();
                    }
                  }}
                >
                  {item.showValue ? <EyeOff size={15} /> : <Eye size={15} />}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="dash-m-section">
        <div className="dash-m-section-row">
          <h3 className="dash-m-section-title">
            {texts.loansNearingExpiry || 'Loans Nearing Expiry (Next 30 Days)'}
          </h3>
          <button type="button" className="dash-m-view-all" onClick={onViewLoans}>
            {texts.viewAll || 'View All'}
          </button>
        </div>
        {loading && expiringLoansData.length === 0 ? (
          <div className="dash-m-empty">
            <RefreshCw size={18} className="dash-m-spin" />
            Loading...
          </div>
        ) : expiringLoansData.length === 0 ? (
          <div className="dash-m-empty success">
            <CalendarDays size={28} />
            {texts.noExpiringLoansCaughtUp ||
              'Great! No loans expiring in the next 30 days. You’re all caught up.'}
          </div>
        ) : (
          <button type="button" className="dash-m-expiry" onClick={onViewLoans}>
            <span className="dash-m-expiry-icon">
              <Hourglass size={18} />
            </span>
            <span className="dash-m-expiry-text">
              <span className="dash-m-expiry-title">
                {expiringLoansData.length} loan(s) expiring soon
              </span>
              <span className="dash-m-expiry-sub">Tap to review</span>
            </span>
            <span className="dash-m-chevron">
              <ChevronRight size={16} />
            </span>
          </button>
        )}
      </div>

      <div className="dash-m-section">
        <div className="dash-m-section-row">
          <h3 className="dash-m-section-title">
            {texts.recentOverduePayments || 'Recent Overdue Payments'}
          </h3>
          <button type="button" className="dash-m-view-all" onClick={onViewLoans}>
            {texts.viewAll || 'View All'}
          </button>
        </div>

        {loading && overduePaymentsData.length === 0 ? (
          <div className="dash-m-empty">
            <RefreshCw size={18} className="dash-m-spin" />
            Loading...
          </div>
        ) : overduePaymentsData.length === 0 ? (
          <div className="dash-m-empty">
            {texts.noOverduePayments || 'No overdue payments'}
          </div>
        ) : (
          <div className="dash-m-recent-list">
            {overduePaymentsData.slice(0, 5).map((loan, index) => {
              const loanId = loan.loanId || loan.id;
              return (
                <button
                  type="button"
                  key={loanId || index}
                  className="dash-m-recent"
                  onClick={() => onOpenLoan(loanId, loan)}
                >
                  <span
                    className="dash-m-avatar"
                    style={{
                      background: `${avatarColors[index % avatarColors.length]}22`,
                      color: avatarColors[index % avatarColors.length]
                    }}
                  >
                    <User size={18} />
                  </span>
                  <span className="dash-m-recent-main">
                    <span className="dash-m-recent-id">{loan.loanNumber || 'N/A'}</span>
                    <span className="dash-m-recent-name">{loan.borrowerName || 'Unknown'}</span>
                  </span>
                  <span className="dash-m-recent-meta">
                    <span className="dash-m-recent-amount">{formatCurrency(loan.dueAmount)}</span>
                    {loan.overdueBy != null && (
                      <span className="dash-m-recent-date">Overdue {loan.overdueBy}d</span>
                    )}
                  </span>
                  <span className="dash-m-chevron">
                    <ChevronRight size={16} />
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardMobile;
